import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {settingsNavigationWidth} from '../settingsCommonConstants';

/*
 * Props.
 */

interface SettingsHomeArticleProps {
  className?: string;
  title: ReactNode;
  description: ReactNode;
  articleColor: PaletteColorsEnum;
  icon: IconName;
  link: string;
  descriptionLines?: number;
  onArticleClick?: Handler;
}

/*
 * Style.
 */

const StyledArticleWrapperDiv = styled.a`
  display: grid;
  grid-template-areas: 'icon . content';
  grid-template-columns: auto 10px 1fr;

  & + & {
    margin-top: 20px;
  }
`;

const StyledArticleIconDiv = styled.div<{$color: PaletteColorsEnum}>`
  grid-area: icon;
  border-radius: 8px;
  background: ${(p) => p.theme.palette[p.$color].shade20};
  width: 46px;
  min-height: 56px;
  margin-bottom: 12px;
  position: relative;
  color: ${(p) => p.theme.greys.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &::before {
    z-index: 0;
    content: '';
    position: absolute;
    height: 30px;
    width: 30px;
    border-radius: 15px;
    background: ${(p) => getPillBackgroundColor(p.$color, p.theme)};
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }
`;

const StyledArticleIconWrapperDiv = styled.div`
  position: relative;
`;

function getPillBackgroundColor(color: PaletteColorsEnum, theme: UserTheme) {
  if (color === PaletteColorsEnum.GREY) {
    return theme.greys.shade90;
  }

  return theme.palette[color].shade40;
}

const StyledContentDiv = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  margin-top: 0px;

  @media (max-width: ${1340 + settingsNavigationWidth}px) {
    justify-content: center;
  }
`;

const StyledContentTitle = styled.h5`
  margin: 0;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  line-height: 1.2;
  position: relative;

  ${hoverSelector} {
    color: ${(p) => p.theme.palette.blue.shade40};
  }
`;

interface StyledContentParagraphProps {
  $descriptionLines?: number;
}

const StyledContentParagraph = styled.p<StyledContentParagraphProps>`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.small};

  display: -webkit-box;
  -webkit-line-clamp: ${(p) => p.$descriptionLines ?? 2};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  line-height: 1.4;
  margin-top: 2px;
`;

const StyledExternalLinkIcon = styled(Icon).attrs({
  name: 'externalLinkSmall',
})`
  opacity: 0;
  position: absolute;
  margin-top: -1px;
  margin-left: 3px;
  display: inline-block;

  ${hoverSelector} {
    opacity: 1;
  }
`;

/*
 * Component.
 */

export const SettingsHomeArticle: FC<SettingsHomeArticleProps> = (props) => (
  <StyledArticleWrapperDiv
    href={props.link}
    rel="noreferrer noopener"
    target="_blank"
    className={`${props.className} ${buildHoverParentClassName()}`}
    // The link trap may completely stop the onClick event so we listen on `mouseup` instead.
    onMouseUp={props.onArticleClick}
  >
    <StyledArticleIconDiv $color={props.articleColor}>
      <StyledArticleIconWrapperDiv>
        <Icon name={props.icon} />
      </StyledArticleIconWrapperDiv>
    </StyledArticleIconDiv>
    <StyledContentDiv>
      <StyledContentTitle>
        {props.title}
        <StyledExternalLinkIcon />
      </StyledContentTitle>
      <StyledContentParagraph $descriptionLines={props.descriptionLines}>
        {props.description}
      </StyledContentParagraph>
    </StyledContentDiv>
  </StyledArticleWrapperDiv>
);
